/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineOfficeBuilding, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Action, Column } from "../../ui";
import { useTradeOrganizationContext } from "../../context/TradeOrganizationContext";
import { pushOneTradeOrganization } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type TradeOrganization = {
  trade_organization_id: number | string;
  name: string;
  [key: string]: unknown;
};

const addRoute = "/trade/org/add";
const editRoute = "/trade/org/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const {
    tradeOrganization,
    count,
    fetchTradeOrganizations,
    deleteTradeOrganization,
    updateTradeOrganizationState,
  } = useTradeOrganizationContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!tradeOrganization) fetchTradeOrganizations(page, pageSize);
  }, [tradeOrganization]);

  const rows = useMemo<TradeOrganization[] | null>(() => {
    if (!Array.isArray(tradeOrganization)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return tradeOrganization as TradeOrganization[];
    return (tradeOrganization as TradeOrganization[]).filter((t) =>
      t.name?.toString().toLowerCase().includes(q),
    );
  }, [tradeOrganization, query]);

  const columns: Column<TradeOrganization>[] = [
    {
      header: "Name",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
            <HiOutlineOfficeBuilding className="h-4 w-4" />
          </span>
          <span className="font-medium text-gray-900">{row.name}</span>
        </div>
      ),
    },
  ];

  const handleDelete = async (row: TradeOrganization) => {
    const response: any = await deleteTradeOrganization(row.trade_organization_id);
    if (response?.status === 200 && Array.isArray(tradeOrganization)) {
      updateTradeOrganizationState(
        (tradeOrganization as TradeOrganization[]).filter(
          (t) => t.trade_organization_id !== row.trade_organization_id,
        ),
      );
    }
  };

  const rowActions = (row: TradeOrganization): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneTradeOrganization(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.name}?`,
        description:
          "The trade organization will be removed from every record that references it.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<TradeOrganization>
      title="Trade Organizations"
      subtitle="Browse, add and manage trade organizations."
      icon={<HiOutlineOfficeBuilding className="h-5 w-5" />}
      actions={[
        { label: "Add organization", variant: "primary", href: addRoute },
      ]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.trade_organization_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search trade organizations…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchTradeOrganizations(p, ps);
        },
      }}
      empty="No trade organizations yet."
    />
  );
};

export default Overview;
